/**
 * The five stages, once.
 *
 * Before this file the funnel was spread across eight places that could
 * disagree — `InitiativesView`, `useInitiativeStats`, `HomeView`, `StagePill`,
 * `theme.ts`, `funnelGates.ts` and `shapeAuthority.ts` each carried their own
 * list, order or labels. Adding a stage meant finding all of them.
 *
 * The division of labour with the API: the API is authoritative for
 * *transitions* — what may move where, and why not. The SPA owns
 * *presentation* — order, labels and the sentence under each one. So there is
 * no `useLookupStages` hook even though `/api/lookup/stages` exists: the order
 * is fixed by the schema seed and the blurbs are the SPA's business.
 *
 * Two shapes here come straight from `keystone.stages` (adr/007). There is no
 * `idea` stage — an idea lives on the board until it is promoted, so G0 is
 * checked when an initiative is CREATED rather than on a move. And closure
 * carries its outcome in the stage, so there is no `Outcome` type.
 *
 * Design v2 names them Shape, Allocate, Implement and Close, and shows Close
 * as ONE stage whose outcome is Succeeded or Failed. The two closed codes stay
 * on the wire; `STAGE_GROUPS` and `stageGroupOf` fold them into one.
 */

export const STAGE_CODES = [
  'shaped',
  'allocate',
  'in_progress',
  'closed_success',
  'closed_fail',
] as const
export type StageCode = (typeof STAGE_CODES)[number]

export interface FunnelStage {
  code: StageCode
  label: string
  /** One sentence. What it means to be here, and what it takes to leave. */
  blurb: string
  /** The gate out of this stage, for the board's dividers. Null once closed. */
  gate: 'G1' | 'G2' | null
  /** What that gate asks for, in three words. Null wherever `gate` is. */
  gateName: string | null
}

/**
 * Funnel order, top to bottom. The two closed stages are the bottom of the
 * funnel, not something beside it: an initiative that stopped is still a
 * record of a decision, and adr/002 makes the result the point of closing.
 */
export const FUNNEL: readonly FunnelStage[] = [
  {
    code: 'shaped',
    label: 'Shape',
    blurb: 'The problem and its value are written down, with a success measure.',
    gate: 'G1',
    gateName: 'Name what it needs',
  },
  {
    code: 'allocate',
    label: 'Allocate',
    blurb: 'Roles and hours are named. Waiting for the allocator.',
    gate: 'G2',
    gateName: 'Commit the people',
  },
  {
    code: 'in_progress',
    label: 'Implement',
    blurb: "People are allocated. Work runs in the team's own tool.",
    gate: null,
    gateName: null,
  },
  {
    code: 'closed_success',
    label: 'Close · Succeeded',
    blurb: 'Closed, and it worked. The evidence says what came of it.',
    gate: null,
    gateName: null,
  },
  {
    code: 'closed_fail',
    label: 'Close · Failed',
    blurb: 'Closed, and it did not work. The evidence says why.',
    gate: null,
    gateName: null,
  },
]

export const STAGE_LABEL: Record<StageCode, string> = Object.fromEntries(
  FUNNEL.map(s => [s.code, s.label]),
) as Record<StageCode, string>

/** A closed initiative's outcome, the field inside Close. */
export const OUTCOME_LABEL: Record<'closed_success' | 'closed_fail', string> = {
  closed_success: 'Succeeded',
  closed_fail: 'Failed',
}

/** The four stages the site shows: the two closed codes are one, Close. */
export type StageGroup = 'shaped' | 'allocate' | 'in_progress' | 'closed'

export const STAGE_GROUPS: readonly { key: StageGroup; label: string }[] = [
  { key: 'shaped', label: 'Shape' },
  { key: 'allocate', label: 'Allocate' },
  { key: 'in_progress', label: 'Implement' },
  { key: 'closed', label: 'Close' },
]

export function stageGroupOf(stage: StageCode): StageGroup {
  return isClosed(stage) ? 'closed' : (stage as Exclude<StageGroup, 'closed'>)
}

/** A group's count from per-code counts. Close sums both closed codes. */
export function groupCount(byStage: Record<string, number>, group: StageGroup): number {
  return Object.entries(byStage)
    .filter(([code]) => stageGroupOf(code as StageCode) === group)
    .reduce((sum, [, n]) => sum + n, 0)
}

/** The forward path. The closed stages are absent: they are closed into. */
const ADVANCE_CHAIN: readonly StageCode[] = ['shaped', 'allocate', 'in_progress']

export function nextStage(stage: StageCode): StageCode | null {
  const i = ADVANCE_CHAIN.indexOf(stage)
  return i === -1 ? null : (ADVANCE_CHAIN[i + 1] ?? null)
}

export function previousStage(stage: StageCode): StageCode | null {
  const i = ADVANCE_CHAIN.indexOf(stage)
  return i <= 0 ? null : (ADVANCE_CHAIN[i - 1] ?? null)
}

export function isClosed(stage: StageCode): boolean {
  return stage.startsWith('closed_')
}
